// Overlay full-screen para el step `select-vistas` del Catálogo IA.
// Se renderiza encima de TODO (incluyendo el sheet del preset) cuando
// el step coincide. Layout:
//   • Header: Volver (back) + título + 2 pills "Con modelo" / "Solo producto".
//   • Grid: 3 columnas con las vistas de la categoría activa.
//   • Footer: strip de seleccionadas (cada thumb con X para deseleccionar)
//             + botón "Siguiente".
// Mobile: `fixed inset-0`. Desktop: `fixed inset-y-0 right-0` +
// `left: sidebarWidth` para que el sidebar siga visible.

import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Plus, Trash2, X } from 'lucide-react';
import type { PoseV2 } from '../posesApi';
import type { MockProductV2 } from '../mockData';
import {
  VISTAS_SOLO_PRODUCTO,
  isVistaCompatibleConModelView,
  resolveVistaImageUrl,
  type VistaCatalogo,
} from '../vistasCatalogo';

export interface VistaPickerFullscreenProps {
  open: boolean;
  variant: 'mobile' | 'desktop';
  sidebarWidth: number;
  allPoses: PoseV2[];
  /** Poses custom (muñeco) de la empresa — tab "Mis poses". */
  customPoses: PoseV2[];
  /** Crear una pose custom desde acá (sube foto → muñeco). */
  onUploadCustomPose: (file: File) => void;
  /** true mientras se procesa la subida de una pose custom. */
  uploadingCustomPose: boolean;
  /** Borrar una pose custom propia. */
  onDeleteCustomPose: (pose: PoseV2) => void;
  selectedProducts: MockProductV2[];
  selectedVistas: VistaCatalogo[];
  onChange: (next: VistaCatalogo[]) => void;
  onBack: () => void;
  onNext: () => void;
}

export const VistaPickerFullscreen: React.FC<VistaPickerFullscreenProps> = ({
  open,
  variant,
  sidebarWidth,
  allPoses,
  customPoses,
  onUploadCustomPose,
  uploadingCustomPose,
  onDeleteCustomPose,
  selectedProducts,
  selectedVistas,
  onChange,
  onBack,
  onNext,
}) => {
  const [activeCategory, setActiveCategory] = useState<
    'con-modelo' | 'solo-producto' | 'mis-pose'
  >('con-modelo');
  // Input file oculto para "Crear pose" (tab Mis poses).
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Animación de entrada: subtle fade + slide-up al abrirse. Damos un
  // tick para que el primer render aplique los estilos "iniciales" y
  // el segundo (post-rAF) los finales — así la transición CSS se
  // dispara. Declarado ARRIBA del early return de `!open` para no
  // violar las Rules of Hooks.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Poses 1-persona transformadas a VistaCatalogo. Arrastramos
  // `areasPrincipales` (tipos de producto sugeridos de la pose) para que
  // el filtro de compatibilidad pueda decidir con qué productos aplica.
  const posesAsVistas: VistaCatalogo[] = React.useMemo(
    () =>
      allPoses
        .filter((p) => (p.cantidadPersonas ?? 1) === 1)
        .map((p) => ({
          id: p.id,
          nombre: p.nombre,
          imageUrl: p.imageUrl,
          kind: 'con-modelo' as const,
          areasPrincipales: p.areasPrincipales,
        })),
    [allPoses],
  );
  // Poses filtradas a las compatibles con AL MENOS un producto
  // seleccionado, mirando `areasPrincipales`. Así, si el user eligió un
  // zapato y una remera, no mostramos poses que enfocan la cabeza.
  const posesCompatibles = React.useMemo(
    () =>
      posesAsVistas.filter((v) =>
        selectedProducts.some((p) =>
          isVistaCompatibleConModelView(v, p.modelView),
        ),
      ),
    [posesAsVistas, selectedProducts],
  );
  // Vistas solo-producto filtradas a las compatibles con AL MENOS un
  // producto seleccionado.
  const vistasSoloProductoCompatibles = React.useMemo(
    () =>
      VISTAS_SOLO_PRODUCTO.filter((v) =>
        selectedProducts.some((p) =>
          isVistaCompatibleConModelView(v, p.modelView),
        ),
      ),
    [selectedProducts],
  );

  const isSelected = (vista: VistaCatalogo) =>
    selectedVistas.some((v) => v.kind === vista.kind && v.id === vista.id);
  const toggle = (vista: VistaCatalogo) => {
    if (isSelected(vista)) {
      onChange(
        selectedVistas.filter(
          (v) => !(v.kind === vista.kind && v.id === vista.id),
        ),
      );
    } else {
      onChange([...selectedVistas, vista]);
    }
  };
  const removeFromSelected = (vista: VistaCatalogo) =>
    onChange(
      selectedVistas.filter(
        (v) => !(v.kind === vista.kind && v.id === vista.id),
      ),
    );

  // Poses custom (muñeco) como vistas. Son universales: NO se filtran por
  // producto (el cliente las creó a propósito). Llevan `filename` para que
  // la generación arme `poses_custom:[{id,filename}]`.
  const customPosesAsVistas: VistaCatalogo[] = React.useMemo(
    () =>
      customPoses.map((p) => ({
        id: p.id,
        nombre: p.nombre,
        imageUrl: p.imageUrl,
        kind: 'mis-pose' as const,
        filename: p.filename,
      })),
    [customPoses],
  );

  const currentVistas =
    activeCategory === 'con-modelo'
      ? posesCompatibles
      : activeCategory === 'solo-producto'
        ? vistasSoloProductoCompatibles
        : customPosesAsVistas;

  // Resuelve el thumbnail de una vista probando los `model_view` de los
  // productos seleccionados — si alguno tiene un override matcheado en
  // `imageUrlByModelView`, lo usamos (p. ej. ropa muestra el plano de
  // prenda en vez de la zapatilla genérica). Sino cae al `imageUrl`
  // default.
  const resolveImageForSelected = (vista: VistaCatalogo): string | null => {
    if (!vista.imageUrlByModelView) return vista.imageUrl;
    for (const product of selectedProducts) {
      const url = resolveVistaImageUrl(vista, product.modelView);
      if (url && url !== vista.imageUrl) return url;
    }
    return vista.imageUrl;
  };

  const renderVistaCard = (vista: VistaCatalogo) => {
    const selected = isSelected(vista);
    const thumbUrl = resolveImageForSelected(vista);
    return (
      <button
        key={`${vista.kind}-${vista.id}`}
        type="button"
        onClick={() => toggle(vista)}
        className={`relative aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 active:scale-[0.98] transition-all ${
          selected ? 'ring-2 ring-brand-primary' : 'ring-1 ring-black/[0.06]'
        }`}
      >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={vista.nombre}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40 text-[12.5px]">
            sin preview
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
        <p className="absolute bottom-2 left-2 right-2 text-left text-white text-[12.5px] font-medium leading-tight drop-shadow">
          {vista.nombre}
        </p>
        {selected && (
          <span className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-brand-primary text-white">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
        )}
      </button>
    );
  };

  // Card "Crear pose" del tab Mis poses — dispara el file picker; spinner
  // mientras el muñeco se genera.
  const renderCreatePoseCard = () => (
    <button
      type="button"
      onClick={() => fileRef.current?.click()}
      disabled={uploadingCustomPose}
      className="relative aspect-[3/4] rounded-xl border-2 border-dashed border-brand-primary/40 bg-brand-primary/[0.04] hover:bg-brand-primary/[0.08] disabled:opacity-60 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2 text-brand-primary transition-colors active:scale-[0.98]"
    >
      {uploadingCustomPose ? (
        <>
          <span className="w-6 h-6 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
          <span className="text-[11.5px] font-medium px-2 text-center leading-tight">
            Creando…
          </span>
        </>
      ) : (
        <>
          <Plus className="w-6 h-6" strokeWidth={2.5} />
          <span className="text-[12.5px] font-semibold px-2 text-center leading-tight">
            Crear pose
          </span>
        </>
      )}
    </button>
  );

  // Card de una pose custom — toggle de selección + botón borrar. Es un
  // <div> (no <button>) para poder anidar el botón de borrar adentro.
  const renderMisPoseCard = (pose: PoseV2) => {
    const vista: VistaCatalogo = {
      id: pose.id,
      nombre: pose.nombre,
      imageUrl: pose.imageUrl,
      kind: 'mis-pose',
      filename: pose.filename,
    };
    const selected = isSelected(vista);
    return (
      <div
        key={`mis-pose-${pose.id}`}
        role="button"
        tabIndex={0}
        onClick={() => toggle(vista)}
        className={`relative aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 cursor-pointer active:scale-[0.98] transition-all ${
          selected ? 'ring-2 ring-brand-primary' : 'ring-1 ring-black/[0.06]'
        }`}
      >
        {pose.imageUrl ? (
          <img
            src={pose.imageUrl}
            alt={pose.nombre}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40 text-[12.5px]">
            sin preview
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
        <p className="absolute bottom-2 left-2 right-2 text-left text-white text-[12.5px] font-medium leading-tight drop-shadow">
          {pose.nombre}
        </p>
        {/* Borrar — esquina superior izquierda. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteCustomPose(pose);
          }}
          aria-label={`Borrar ${pose.nombre}`}
          className="absolute top-2 left-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 active:scale-95 transition-all"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        {selected && (
          <span className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-brand-primary text-white">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
        )}
      </div>
    );
  };

  const isMobile = variant === 'mobile';

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-surface-mobile md:bg-surface-content md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden flex flex-col transition-[opacity,transform] duration-200 ease-out"
      style={{
        // El picker cubre toda la pantalla (top: 0) para que NO se vea
        // el sheet del choose-product detrás. El chrome flotante del
        // Sidebar mobile (back/menu/credits pills) queda visible
        // encima del bg blanco del propio picker — mismo patrón que
        // las otras pantallas con `mobileHeaderTransparent: true`.
        left: isMobile ? 0 : sidebarWidth,
        right: 0,
        top: 0,
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0)' : 'translateY(8px)',
      }}
    >
      {/* Cobertura blanca opaca en la zona del chrome flotante mobile.
          Los pills del Sidebar (back/menu/credits) son `bg-white/95`
          translúcidos, así que sin esto las cards que scrollean por
          debajo se ven filtrándose entre los gaps de los pills. Esta
          capa absoluta dentro del wrapper queda detrás del chrome del
          Sidebar (z-50) pero encima del scroll del picker. */}
      {isMobile && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 bg-white pointer-events-none z-[5]"
          style={{
            height: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
          }}
        />
      )}

      {/* Desktop only: sticky top bar con Volver (left) + Siguiente (right).
          El botón Siguiente flota arriba a la derecha siempre visible —
          el user puede confirmar sin scrollear al fondo. */}
      {!isMobile && (
        <div className="shrink-0 z-[20] px-8 pt-5 pb-4 flex items-center justify-between bg-white">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={selectedVistas.length === 0}
            className="inline-flex items-center gap-2 py-2.5 px-5 rounded-full bg-brand-primary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors"
          >
            Siguiente
            {selectedVistas.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/25 text-[12.5px] font-semibold">
                {selectedVistas.length}
              </span>
            )}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* Scrollable area: en mobile el título + pills viven adentro
          del scroll (las pills `sticky top-0` para que queden pegadas
          arriba al hacer scroll). En desktop pasa lo mismo: el título
          scrollea, los pills se quedan. */}
      <div className="flex-1 overflow-y-auto">
        {/* Title block — scrollea. En mobile dejamos padding-top extra
            para que el chrome flotante del Sidebar no tape el título. */}
        <div
          className="px-5 sm:px-8 pb-3 sm:pt-6"
          style={{
            paddingTop: isMobile
              ? 'calc(env(safe-area-inset-top, 0px) + 3.5rem + 1rem)'
              : undefined,
          }}
        >
          <h1 className="text-[22px] sm:text-[26px] font-semibold text-brand-black/90 leading-tight">
            Elegí las vistas
          </h1>
          <p className="text-[13.5px] sm:text-[14.5px] text-[#3C3C43]/60 mt-1">
            Vamos a generar una imagen por cada vista × producto compatible.
          </p>
        </div>

        {/* Sticky pills — quedan pegadas al tope cuando el user
            scrollea. En mobile, ofseteamos `top` por el safe-area-inset
            + altura del header del Sidebar para que no se solapen con
            el chrome flotante (back/menu/credits pills). */}
        <div
          className="sticky z-10 px-5 sm:px-8 py-3 bg-white flex items-center gap-2"
          style={{
            top: isMobile
              ? 'calc(env(safe-area-inset-top, 0px) + 3.5rem)'
              : 0,
          }}
        >
          <button
            type="button"
            onClick={() => setActiveCategory('con-modelo')}
            className={`inline-flex items-center px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
              activeCategory === 'con-modelo'
                ? 'bg-brand-black text-white border-brand-black'
                : 'bg-white text-brand-black/85 border-brand-black/30 hover:bg-brand-black/[0.04]'
            }`}
          >
            Con modelo
            {posesAsVistas.length > 0 && (
              <span
                className={`ml-1.5 text-[12.5px] ${
                  activeCategory === 'con-modelo'
                    ? 'text-white/80'
                    : 'text-[#3C3C43]/55'
                }`}
              >
                {posesAsVistas.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory('solo-producto')}
            className={`inline-flex items-center px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
              activeCategory === 'solo-producto'
                ? 'bg-brand-black text-white border-brand-black'
                : 'bg-white text-brand-black/85 border-brand-black/30 hover:bg-brand-black/[0.04]'
            }`}
          >
            Solo producto
            {vistasSoloProductoCompatibles.length > 0 && (
              <span
                className={`ml-1.5 text-[12.5px] ${
                  activeCategory === 'solo-producto'
                    ? 'text-white/80'
                    : 'text-[#3C3C43]/55'
                }`}
              >
                {vistasSoloProductoCompatibles.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory('mis-pose')}
            className={`inline-flex items-center px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
              activeCategory === 'mis-pose'
                ? 'bg-brand-black text-white border-brand-black'
                : 'bg-white text-brand-black/85 border-brand-black/30 hover:bg-brand-black/[0.04]'
            }`}
          >
            Mis poses
            {customPosesAsVistas.length > 0 && (
              <span
                className={`ml-1.5 text-[12.5px] ${
                  activeCategory === 'mis-pose'
                    ? 'text-white/80'
                    : 'text-[#3C3C43]/55'
                }`}
              >
                {customPosesAsVistas.length}
              </span>
            )}
          </button>
        </div>

        {/* Grid. Padding-bottom extra en desktop para que las cards no
            queden ocultas detrás del floating strip de seleccionadas. */}
        <div className="px-5 sm:px-8 py-5 sm:pb-32">
          {activeCategory === 'mis-pose' ? (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadCustomPose(f);
                  e.currentTarget.value = '';
                }}
              />
              <p className="text-[13px] text-[#3C3C43]/55 mb-3 -mt-1">
                Subí una foto de una pose y Atlas crea un muñeco que la copia.
                Después la usás para armar tu catálogo.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                {renderCreatePoseCard()}
                {customPoses.map((p) => renderMisPoseCard(p))}
              </div>
            </>
          ) : currentVistas.length === 0 ? (
            <p className="text-[14px] text-[#3C3C43]/55 mt-4">
              No hay vistas disponibles en esta categoría para los productos
              elegidos.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
              {currentVistas.map(renderVistaCard)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile footer: thumbnail strip + botón Siguiente full-width.
          En desktop esto se reemplaza por el botón en la sticky top bar
          + un floating strip estilo MultiSelectedStrip (abajo). */}
      {isMobile && (
        <div
          className="shrink-0 border-t border-black/[0.06] bg-white px-5 pt-3 pb-4"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          }}
        >
          {selectedVistas.length > 0 && (
            <div className="flex items-center gap-2 mb-3 overflow-x-auto scrollbar-hide -mx-2 px-2 pt-1.5">
              {selectedVistas.map((v) => (
                <div key={`${v.kind}-${v.id}`} className="shrink-0 relative">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-neutral-100 ring-1 ring-black/[0.06]">
                    {v.imageUrl ? (
                      <img
                        src={v.imageUrl}
                        alt={v.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#3C3C43]/40 text-[11px] text-center px-1">
                        {v.nombre}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromSelected(v);
                    }}
                    aria-label={`Quitar ${v.nombre}`}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 inline-flex items-center justify-center rounded-full bg-brand-black text-white shadow-md hover:bg-brand-black/80 active:scale-95 transition-all"
                  >
                    <X className="w-3 h-3" strokeWidth={3} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={selectedVistas.length === 0}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold transition-colors"
          >
            Siguiente
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Desktop floating strip de seleccionadas — mismo estilo que el
          MultiSelectedStrip de multi-template (rounded-2xl + shadow,
          centrado, abajo). Solo se muestra cuando hay seleccionadas.
          Usamos `flex justify-center` con `left/right: 0` en vez de
          `translateX(-50%)` para que la animación `animate-mode-swap`
          (que setea su propio `transform`) no pise el centrado. */}
      {!isMobile && selectedVistas.length > 0 && (
        <div
          className="absolute left-0 right-0 z-30 px-8 pointer-events-none animate-mode-swap flex justify-center"
          style={{ bottom: '32px' }}
        >
          <div className="pointer-events-auto bg-white rounded-3xl shadow-xl ring-1 ring-black/[0.05] px-6 py-5 flex items-center gap-4 max-w-[720px] w-full">
            <div className="flex items-center gap-3 overflow-x-auto flex-1 scrollbar-hide pt-1.5 -my-1.5">
              {selectedVistas.map((v) => (
                <div key={`${v.kind}-${v.id}`} className="shrink-0 relative">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-neutral-100 ring-1 ring-black/[0.05]">
                    {v.imageUrl ? (
                      <img
                        src={v.imageUrl}
                        alt={v.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#3C3C43]/40 text-[11px] text-center px-1.5">
                        {v.nombre}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFromSelected(v)}
                    aria-label={`Quitar ${v.nombre}`}
                    className="absolute -top-1.5 -right-1.5 w-6 h-6 inline-flex items-center justify-center rounded-full bg-black/65 text-white hover:bg-black/80 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
